import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Grid,
  Group,
  Stack,
  Text,
  Title,
  SimpleGrid,
  Skeleton,
  Timeline,
  Anchor,
  Divider,
  Drawer,
  ScrollArea,
  Badge,
  Box,
  Rating,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconArrowLeft,
  IconExternalLink,
  IconMapPin,
  IconPhone,
  IconMessage,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { tripsApi } from "../../api/trips";
import { rideTypesApi } from "../../api/rideTypes";
import { supportApi } from "../../api/support";
import { TRACK_BASE_URL } from "../../config/env";
import { PageHeader } from "../../components/common/PageHeader";
import { TripActions } from "../../components/console/TripActions";
import { TripDispatchCard } from "../../components/console/TripDispatchCard";
import { TripRouteReplayCard } from "../../components/console/TripRouteReplayCard";
import { MoneyWaterfall } from "../../components/console/MoneyWaterfall";
import { TripCashbackCard } from "../../components/finance/TripCashbackCard";
import { PartPaymentCard } from "../../components/console/PartPaymentCard";
import { StatusBadge } from "../../components/common/StatusBadge";
import { QueryError } from "../../components/common/QueryError";
import { DriverLink, UserLink } from "../../components/common/EntityLink";
import { formatMoney, formatDateTime, tripRef, tripFareDetail } from "../../lib/format";
import { humanize } from "../../lib/labels";

const LIVE_STATUSES = new Set([
  "accepted",
  "driverArrived",
  "inProgress",
  "intermediateStopArrived",
  "intermediateStopSkipped",
  "destinationArrived",
]);

function Field({ label, value }: { label: string; value?: string | number }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" fw={600} tt="uppercase">
        {label}
      </Text>
      <Text size="sm">{value || "—"}</Text>
    </Stack>
  );
}

function RatingBlock({
  label,
  r,
}: {
  label: string;
  r?: { rating?: number; message?: string };
}) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" fw={600} tt="uppercase">
        {label}
      </Text>
      {r?.rating ? (
        <>
          <Rating value={r.rating} readOnly fractions={2} size="sm" />
          {r.message && (
            <Text size="sm" c="dimmed">
              “{r.message}”
            </Text>
          )}
        </>
      ) : (
        <Text size="sm" c="dimmed">
          No rating
        </Text>
      )}
    </Stack>
  );
}

export function TripDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const { data: trip, isLoading, error, refetch } = useQuery({
    queryKey: ["trip", id],
    queryFn: () => tripsApi.get(id),
    enabled: !!id,
  });

  const [chatOpen, { open: openChat, close: closeChat }] = useDisclosure(false);
  const chat = useQuery({
    queryKey: ["trip-messages", id],
    queryFn: () => tripsApi.messages(id),
    enabled: chatOpen && !!id,
  });
  const tripQueries = useQuery({
    queryKey: ["trip-queries", id],
    queryFn: () => supportApi.listQueries({ tripID: id, limit: 50 }),
    enabled: !!id,
  });
  const rideTypes = useQuery({
    queryKey: ["ride-types", "all"],
    queryFn: () => rideTypesApi.list(),
  });
  const tripLostItems = useQuery({
    queryKey: ["trip-lost-items", id],
    queryFn: () => supportApi.lostItemsByTrip(id),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <Stack>
        <Skeleton h={40} w={240} />
        <Skeleton h={240} />
      </Stack>
    );
  }
  if (error || !trip) {
    return <QueryError error={error ?? new Error("Not found")} onRetry={refetch} />;
  }

  // Settled fare (actual charge) when the trip was charged; falls back to the
  // upfront quote for trips still in progress. Cancelled trips have neither —
  // nothing was ever charged, so tripFareDetail returns undefined for them.
  const fare = tripFareDetail(trip);
  const isSettledFare = !!trip.paymentDetail?.pricingDetail;
  const settled = trip.paymentDetail?.pricingDetail;
  const isCancelled = trip.tripStatus === "cancelled";
  const route =
    (trip.finalTripRoute?.length ? trip.finalTripRoute : trip.initialTripRoute) ??
    [];
  const v = trip.driverDetail?.vehicleDetail;
  const distance = trip.statistics?.actualDistance ?? trip.statistics?.estimatedDistance;
  const duration = trip.statistics?.actualDuration ?? trip.statistics?.estimatedDuration;
  const isLive = LIVE_STATUSES.has(trip.tripStatus ?? "");
  // A trip served by a driver of another ride type ("also serves", e.g. a
  // Haraka trip by an XL driver). The fare is still the trip's ride type.
  const driverRideTypeId = v?.vehicleCategoryID ? String(v.vehicleCategoryID) : null;
  const servedByOtherRideType =
    driverRideTypeId && trip.rideTypeDetail?.id && driverRideTypeId !== String(trip.rideTypeDetail.id)
      ? (rideTypes.data?.items.find((rt) => rt._id === driverRideTypeId)?.name?.en ?? "another ride type")
      : null;
  const riderPhone = trip.passengerDetail?.phoneNumber;
  const driverPhone = trip.driverDetail?.phoneNumber;

  return (
    <>
      <Button
        variant="subtle"
        color="gray"
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => navigate("/trips")}
        mb="sm"
        pl={0}
      >
        Back to trips
      </Button>

      <PageHeader
        title={`Trip ${tripRef(trip)}`}
        subtitle={formatDateTime(trip.createdAt)}
        actions={
          <Group gap="xs">
            {riderPhone && (
              <Button
                component="a"
                href={`tel:${riderPhone}`}
                variant="light"
                color="gray"
                leftSection={<IconPhone size={16} />}
              >
                Rider
              </Button>
            )}
            {driverPhone && (
              <Button
                component="a"
                href={`tel:${driverPhone}`}
                variant="light"
                color="gray"
                leftSection={<IconPhone size={16} />}
              >
                Driver
              </Button>
            )}
            <Button
              variant="light"
              leftSection={<IconMessage size={16} />}
              onClick={openChat}
            >
              Chat
            </Button>
            {/* Cancel / reassign / force-complete (role + status gated). */}
            <TripActions
              onDone={() => refetch()}
              trip={{
                id: trip._id,
                status: trip.tripStatus ?? "",
                paymentMode: trip.paymentDetail?.paymentMode,
                rideTypeId: trip.rideTypeDetail?.id,
                pickup: trip.initialTripRoute?.[0]
                  ? { lat: trip.initialTripRoute[0].latitude, lng: trip.initialTripRoute[0].longitude }
                  : null,
                driverId: trip.driverDetail?.driverID,
                partPayment: trip.paymentDetail?.split
                  ? {
                      walletAmount: trip.paymentDetail.split.walletAmount,
                      cashAmount: trip.paymentDetail.split.cashAmount,
                      walletRefunded: !!trip.paymentDetail.split.walletRefundedAt,
                    }
                  : null,
              }}
            />
            {trip.shareToken && (
              <Anchor
                href={`${TRACK_BASE_URL}/${trip.shareToken}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="light" leftSection={<IconExternalLink size={16} />}>
                  Tracker
                </Button>
              </Anchor>
            )}
          </Group>
        }
      />

      {servedByOtherRideType && (
        <Badge variant="light" color="violet" mb="md" tt="none" size="lg">
          {trip.rideTypeDetail?.info?.name?.en ?? "This"} trip served by a {servedByOtherRideType} driver
        </Badge>
      )}

      {isLive && trip.shareToken && (
        <Card withBorder radius="md" p={0} mb="md" style={{ overflow: "hidden" }}>
          <Group justify="space-between" px="md" py="xs">
            <Group gap={6}>
              <Badge color="red" variant="filled">
                LIVE
              </Badge>
              <Text size="sm" fw={600}>
                Driver location
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              {humanize(trip.tripStatus)}
            </Text>
          </Group>
          <iframe
            src={`${TRACK_BASE_URL}/${trip.shareToken}`}
            title="Live trip tracking"
            style={{ width: "100%", height: 460, border: 0, display: "block" }}
          />
        </Card>
      )}

      <Grid>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Card withBorder radius="md" padding="lg" mb="md">
            <Group justify="space-between" mb="md">
              <Title order={5}>Overview</Title>
              <StatusBadge status={trip.tripStatus} />
            </Group>
            <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
              <Stack gap={2}>
                <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                  Rider
                </Text>
                <UserLink
                  user={trip.passengerDetail?.passengerID}
                  name={trip.passengerDetail?.name}
                />
              </Stack>
              <Field label="Rider phone" value={trip.passengerDetail?.phoneNumber} />
              <Stack gap={2}>
                <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                  Driver
                </Text>
                <DriverLink
                  driver={trip.driverDetail?.driverID}
                  name={trip.driverDetail?.name}
                />
              </Stack>
              <Field label="Driver phone" value={trip.driverDetail?.phoneNumber} />
              <Field
                label="Vehicle"
                value={
                  v ? `${v.make ?? ""} ${v.model ?? ""}`.trim() || undefined : undefined
                }
              />
              <Field label="Plate" value={v?.vehicleRegistrationNumber} />
              <Field
                label="Payment mode"
                value={
                  trip.paymentDetail?.split
                    ? "Wallet + cash"
                    : trip.paymentDetail?.paymentMode
                      ? humanize(trip.paymentDetail.paymentMode)
                      : undefined
                }
              />
              <Field
                label="Payment status"
                value={
                  trip.tripStatus === "completed"
                    ? "Paid"
                    : trip.tripStatus === "cancelled"
                      ? "—"
                      : trip.paymentDetail?.dpoMetadata?.status
                        ? humanize(trip.paymentDetail.dpoMetadata.status)
                        : "Pending"
                }
              />
              <Field
                label="Distance"
                value={distance != null ? `${Number(distance.toFixed(2))} km` : undefined}
              />
              <Field
                label="Duration"
                value={duration != null ? `${duration} min` : undefined}
              />
            </SimpleGrid>
          </Card>

          {trip.tripStatus === "cancelled" && trip.cancelDetail && (
            <Card withBorder radius="md" padding="lg" mb="md">
              <Title order={5} mb="md">
                Cancellation
              </Title>
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                <Field
                  label="Cancelled by"
                  value={humanize(trip.cancelDetail.createdByInfo?.userType)}
                />
                <Field
                  label="When"
                  value={formatDateTime(trip.cancelDetail.cancelledAt)}
                />
                <Field label="Reason" value={trip.cancelDetail.reason} />
                {trip.cancelDetail.cancellationFee ? (
                  <Field
                    label="Cancellation fee"
                    value={`${formatMoney(trip.cancelDetail.cancellationFee)} · ${humanize(
                      trip.cancelDetail.cancellationFeeStatus ?? "pending"
                    )}`}
                  />
                ) : null}
              </SimpleGrid>
            </Card>
          )}

          <TripDispatchCard tripId={trip._id} />

          <TripRouteReplayCard tripId={trip._id} />

          <Card withBorder radius="md" padding="lg">
            <Title order={5} mb="md">
              Route
            </Title>
            {route.length ? (
              <Timeline active={route.length} bulletSize={18} lineWidth={2}>
                {route.map((stop, i) => (
                  <Timeline.Item
                    key={i}
                    bullet={<IconMapPin size={12} />}
                    title={i === 0 ? "Pickup" : i === route.length - 1 ? "Destination" : `Stop ${i}`}
                  >
                    <Text size="sm">{stop.address ?? "—"}</Text>
                    {stop.latitude != null && stop.longitude != null && (
                      <Text size="xs" c="dimmed">
                        {stop.latitude.toFixed(5)}, {stop.longitude.toFixed(5)}
                      </Text>
                    )}
                  </Timeline.Item>
                ))}
              </Timeline>
            ) : (
              <Text size="sm" c="dimmed">
                No route recorded.
              </Text>
            )}
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Card withBorder radius="md" padding="lg">
            <Title order={5} mb="md">
              Fare breakdown
              {!isCancelled && !isSettledFare && (
                <Text component="span" size="xs" c="dimmed" fw={400} ml={6}>
                  (estimate)
                </Text>
              )}
            </Title>
            {isCancelled ? (
              <Text size="sm" c="dimmed">
                Trip was cancelled — no fare was charged.
              </Text>
            ) : (
              settled && settled.platformEarning !== undefined ? (
              // Settled trip: show exactly where the rider's money went.
              <MoneyWaterfall
                height={200}
                m={{
                  riderPaid: settled.netFare ?? 0,
                  grossFare: settled.grossFare ?? 0,
                  driverEarning: settled.driverEarning ?? 0,
                  vatOnCommission: settled.vatOnCommission ?? 0,
                  vatOnBookingFee: settled.vatOnBookingFee ?? 0,
                  gatewayFee: settled.paymentGatewayFee ?? 0,
                  platformEarning: settled.platformEarning ?? 0,
                  platformCommission: settled.platformCommission ?? 0,
                  bookingFee: settled.bookingFee ?? 0,
                  latraLevy: settled.latraLevy ?? 0,
                  baseCharge: settled.baseCharge,
                  distanceCharge: settled.distanceCharge,
                  timeCharge: settled.timeCharge,
                  roundingDifference: settled.roundingDifference ?? 0,
                  discount: settled.discount ?? 0,
                  tolls: settled.tollFee ?? 0,
                  waitingFee: settled.waitingFee ?? 0,
                  cancellationFee: settled.cancellationFee ?? 0,
                }}
              />
              ) : (
              <Stack gap="xs">
                <FareRow label="Net fare" value={fare?.netFare} strong />
                <Divider />
                <FareRow label="Platform commission" value={fare?.platformCommission} />
                <FareRow label="Driver earning" value={fare?.driverEarning} />
              </Stack>
              )
            )}
          </Card>
          {trip.paymentDetail?.split && (
            <PartPaymentCard
              split={trip.paymentDetail.split}
              fare={trip.paymentDetail.pricingDetail?.netFare}
              closedUnpaid={trip.paymentDetail.cashSettlement === "adminClosedUnpaid"}
            />
          )}
          <TripCashbackCard tripId={id} completed={trip.tripStatus === "completed"} />
        </Grid.Col>
      </Grid>

      {(trip.passengerRatingForDriver?.rating ||
        trip.driverRatingForPassenger?.rating) && (
        <Card withBorder radius="md" padding="lg" mt="md">
          <Title order={5} mb="md">
            Ratings
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <RatingBlock label="Rider → Driver" r={trip.passengerRatingForDriver} />
            <RatingBlock label="Driver → Rider" r={trip.driverRatingForPassenger} />
          </SimpleGrid>
        </Card>
      )}

      {(!!tripQueries.data?.items?.length || !!tripLostItems.data?.length) && (
        <Card withBorder radius="md" padding="lg" mt="md">
          <Title order={5} mb="md">
            Support & lost items
          </Title>
          <Stack gap="xs">
            {tripQueries.data?.items?.map((q) => (
              <Group
                key={q._id}
                justify="space-between"
                onClick={() => navigate("/queries")}
                style={{ cursor: "pointer" }}
              >
                <Group gap="xs">
                  <Badge variant="light">Query</Badge>
                  <Text size="sm">
                    {humanize(q.queryType)} · {q.ticketNo}
                  </Text>
                </Group>
                <StatusBadge status={q.status} />
              </Group>
            ))}
            {tripLostItems.data?.map((it) => (
              <Group
                key={it._id}
                justify="space-between"
                onClick={() => navigate("/lost-items")}
                style={{ cursor: "pointer" }}
              >
                <Group gap="xs">
                  <Badge variant="light" color="orange">
                    Lost item
                  </Badge>
                  <Text size="sm">
                    {humanize(it.itemType)} · {it.ticketNo}
                  </Text>
                </Group>
                <StatusBadge status={it.status} />
              </Group>
            ))}
          </Stack>
        </Card>
      )}

      <Drawer
        opened={chatOpen}
        onClose={closeChat}
        position="right"
        size="md"
        title={<Text fw={700}>Trip chat</Text>}
        scrollAreaComponent={ScrollArea.Autosize}
      >
        {chat.isLoading ? (
          <Text size="sm" c="dimmed">
            Loading…
          </Text>
        ) : !chat.data?.length ? (
          <Text size="sm" c="dimmed">
            No messages on this trip.
          </Text>
        ) : (
          <Stack gap="sm">
            {chat.data.map((m) => {
              const fromDriver = m.senderType === "DRIVER" || m.senderType === "driver";
              return (
                <Box
                  key={m._id}
                  style={{ alignSelf: fromDriver ? "flex-start" : "flex-end", maxWidth: "85%" }}
                >
                  <Badge size="xs" variant="light" color={fromDriver ? "steel" : "oxblood"} mb={2}>
                    {humanize(m.senderType)}
                  </Badge>
                  <Card withBorder radius="md" padding="xs">
                    <Text size="sm">{m.message ?? humanize(m.predefinedKey)}</Text>
                    <Text size="10px" c="dimmed" mt={2}>
                      {formatDateTime(m.createdAt)}
                    </Text>
                  </Card>
                </Box>
              );
            })}
          </Stack>
        )}
      </Drawer>
    </>
  );
}

function FareRow({
  label,
  value,
  strong,
}: {
  label: string;
  value?: number;
  strong?: boolean;
}) {
  return (
    <Group justify="space-between">
      <Text size="sm" c={strong ? undefined : "dimmed"} fw={strong ? 700 : 400}>
        {label}
      </Text>
      <Text size="sm" fw={strong ? 700 : 500}>
        {formatMoney(value)}
      </Text>
    </Group>
  );
}
